import React, { useCallback, useEffect, useState } from 'react';

import { FormControl } from '@mui/material';

import { ArrowIcon } from '~/components/icons';
import { AdornmentIconButton, TextField } from '~/components/TextField';
import { useUpdateVehicleOptions } from '~/contexts/VehicleOptionsContext';
import { validateEim } from '~/domain/features';
import { IConfigurationOptionGroup } from '~/domain/IConfigurationOptionGroup';
import { IConfigurationRule } from '~/domain/IConfigurationRule';
import { EnterKey } from '~/keyboard';

interface IEimInput {
  defaultValue?: string;
  defaultErrorMessage?: string;
  eimPackSets: IConfigurationOptionGroup[];
  packSets: IConfigurationOptionGroup[];
  rules: IConfigurationRule[];
}

export const EimInput = React.memo<IEimInput>(function EimInput(props) {
  const {
    defaultValue,
    defaultErrorMessage,
    eimPackSets,
    packSets,
    rules,
  } = props;
  const updateVehicleOptions = useUpdateVehicleOptions();
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage || '');
  const [value, setValue] = useState(defaultValue || '');

  const submit = useCallback((eim: string) => {
    const [features, errorMessage] = validateEim(eimPackSets, packSets, rules, eim);
    if (features) {
      updateVehicleOptions({
        eim: value,
        features: features,
      });
      setValue('');
      setErrorMessage('');
    } else {
      setErrorMessage(errorMessage || '');
    }
  }, [eimPackSets, packSets, rules, updateVehicleOptions, value]);

  const handleEimSubmit = () => {
    submit(value);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  useEffect(() => {
    if (defaultValue) {
      const [, errorMessage] = validateEim(eimPackSets, packSets, rules, defaultValue);
      if (errorMessage) {
        setErrorMessage(errorMessage);
      }
    }
  }, [defaultValue, eimPackSets, packSets, rules]);

  return (
    <FormControl sx={{ width: '100%' }}>
      <TextField
        InputProps={{
          endAdornment: (
            <AdornmentIconButton
              onSubmit={handleEimSubmit}
              Icon={ArrowIcon}
              iconProps={{ transform: 'rotate(-90deg)' }}
            />
          ),
        }}
        value={value}
        onChange={handleChange}
        errorMessage={errorMessage}
        onKeyDown={(e) => {
          if (e.key === EnterKey) {
            handleEimSubmit();
          }
        }}
        placeholder="Enter new EIM"
        onSubmit={handleEimSubmit}
        onBlur={() => {
          const [, errorMessage] = validateEim(eimPackSets, packSets, rules, value);
          setErrorMessage(value ? errorMessage || '' : '');
        }}
      />
    </FormControl>
  );
});
